import { actions } from "../../app/state.js";
import { createEmailRecord, listEmailsByEvent, sendEmailViaFunction, EMAIL_SENDING_ENABLED } from "../../services/emailsService.js";
import { fetchContact, fetchPrimaryContactId } from "./EventModal.data.js";
import { $, escapeHtml } from "./EventModal.utils.js";

export function wireEmailsLogic(root, eventId) {
  if (!eventId) return;

  const composeBtn = $(root, "#event-email-compose");
  if (composeBtn && !EMAIL_SENDING_ENABLED) {
    composeBtn.classList.add("hidden");
  } else if (composeBtn) {
    composeBtn.onclick = async () => {
      const primaryContact = await fetchContact(await fetchPrimaryContactId(eventId));

      if (!primaryContact?.email) {
        actions.addToast?.({ type: "warning", message: "El contacto principal no tiene email configurado." });
        return;
      }

      openEmailComposeModal({
        eventId,
        contactId: primaryContact.id,
        toEmail: primaryContact.email,
        onSent: () => renderEmails(root, eventId),
      });
    };
  }

  renderEmails(root, eventId);
}

function openEmailComposeModal({ eventId, contactId, toEmail, onSent }) {
  const modalHtml = `
      <div class="modal-backdrop" id="email-compose-backdrop">
        <div class="modal-dialog">
          <div class="modal-header flex items-center justify-between">
            <h3 class="font-bold text-slate-800 flex items-center gap-2">
              <i class="fa-solid fa-paper-plane text-blue-500"></i>
              Redactar correo
            </h3>
            <button id="email-compose-close" class="text-slate-400 hover:text-slate-600 p-2">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
          
          <div class="modal-body space-y-4">
            <div>
              <label class="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Destinatario</label>
              <div class="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-600 font-medium">
                ${escapeHtml(toEmail)}
              </div>
            </div>
            
            <div>
              <label class="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Asunto</label>
              <input type="text" id="email-subject" class="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all" placeholder="Escribe el asunto...">
            </div>
            
            <div>
              <label class="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Mensaje</label>
              <textarea id="email-body" rows="8" class="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-none" placeholder="Escribe tu mensaje aquí..."></textarea>
            </div>

            <div>
              <label class="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Adjuntos</label>
              <div class="flex flex-col gap-2">
                <div id="email-attachments-list" class="flex flex-wrap gap-2"></div>
                <label class="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 hover:text-slate-600 hover:border-slate-300 cursor-pointer transition-all">
                  <i class="fa-solid fa-paperclip text-sm"></i>
                  <span class="text-xs font-bold">Añadir archivos</span>
                  <input type="file" id="email-attachments-input" class="hidden" multiple>
                </label>
              </div>
            </div>
          </div>
          
          <div class="modal-footer">
            <button id="email-compose-cancel" class="px-5 py-2.5 text-slate-600 font-medium hover:bg-slate-200/50 rounded-xl transition-colors">Cancelar</button>
            <button id="email-send-btn" class="px-6 py-2.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 shadow-lg shadow-slate-900/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              <span>Enviar</span>
              <i class="fa-solid fa-paper-plane text-xs"></i>
            </button>
          </div>
        </div>
      </div>
    `;

  const el = document.createElement("div");
  el.innerHTML = modalHtml;
  document.body.appendChild(el);

  const close = () => el.remove();
  document.getElementById("email-compose-close").onclick = close;
  document.getElementById("email-compose-cancel").onclick = close;

  let selectedFiles = [];

  const updateAttachmentsList = () => {
    const list = document.getElementById("email-attachments-list");
    list.innerHTML = selectedFiles.map((file, index) => `
      <div class="flex items-center gap-2 px-2 py-1 bg-slate-100 border border-slate-200 rounded-lg text-[11px] text-slate-700">
        <i class="fa-solid fa-file text-[10px] text-slate-400"></i>
        <span class="truncate max-w-[120px] font-medium">${escapeHtml(file.name)}</span>
        <button class="text-slate-400 hover:text-red-500 remove-att-btn" data-index="${index}">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
    `).join("");

    list.querySelectorAll(".remove-att-btn").forEach(btn => {
      btn.onclick = () => {
        selectedFiles.splice(parseInt(btn.dataset.index, 10), 1);
        updateAttachmentsList();
      };
    });
  };

  document.getElementById("email-attachments-input").onchange = (e) => {
    const files = Array.from(e.target.files);
    selectedFiles = [...selectedFiles, ...files];
    updateAttachmentsList();
    e.target.value = "";
  };

  const sendBtn = document.getElementById("email-send-btn");
  sendBtn.onclick = async () => {
    const subject = document.getElementById("email-subject").value.trim();
    const body = document.getElementById("email-body").value.trim();

    if (!subject || !body) {
      actions.addToast?.({ type: "warning", message: "Asunto y mensaje son obligatorios." });
      return;
    }

    sendBtn.disabled = true;
    sendBtn.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin mr-2"></i>Enviando...';

    try {
      await sendEmailViaFunction({
        to: toEmail,
        subject,
        body,
        attachments: selectedFiles,
      });

      await createEmailRecord({
        event_id: eventId,
        contact_id: contactId,
        subject,
        body,
        direction: "outbound",
        status: "sent",
      });

      actions.addToast?.({ type: "success", message: "Correo enviado correctamente." });
      close();
      onSent?.();
    } catch (err) {
      console.error(err);
      actions.addToast?.({ type: "error", message: "Error al enviar el correo." });
      sendBtn.disabled = false;
      sendBtn.innerHTML = '<span>Enviar</span><i class="fa-solid fa-paper-plane text-xs"></i>';
    }
  };
}

async function renderEmails(root, eventId) {
  const listEl = $(root, "#event-emails-list");
  const emptyState = $(root, "#event-email-empty");
  const detailsPane = $(root, "#event-email-details");
  if (!listEl) return;

  listEl.innerHTML = `
    <div class="flex flex-col items-center justify-center py-8">
      <i class="fa-solid fa-circle-notch fa-spin text-slate-300 text-2xl mb-2"></i>
    </div>
  `;

  try {
    const emails = await listEmailsByEvent(eventId);
    if (emails.length === 0) {
      listEl.innerHTML = `
        <div class="p-6 text-center">
          <div class="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-2 text-slate-400">
            <i class="fa-solid fa-envelope"></i>
          </div>
          <p class="text-xs text-slate-500">No hay correos registrados.</p>
        </div>
      `;
      return;
    }

    listEl.innerHTML = emails.map(m => {
      const isOutbound = m.direction === "outbound";
      const date = new Date(m.created_at).toLocaleDateString("es-ES", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      const time = new Date(m.created_at).toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
      });

      const icon = isOutbound ? "fa-paper-plane text-blue-500" : "fa-inbox text-emerald-500";
      const bgColor = isOutbound ? "bg-blue-50/50" : "bg-emerald-50/50";

      return `
        <div class="email-list-item cursor-pointer border-b border-slate-200 transition-colors hover:bg-slate-100/50 p-4" data-id="${m.id}">
          <div class="flex items-start gap-3">
             <div class="w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${bgColor} border border-slate-200/50">
               <i class="fa-solid ${icon} text-[10px]"></i>
             </div>
             <div class="flex-1 min-w-0">
                <div class="flex justify-between items-center mb-1">
                   <div class="text-[10px] uppercase font-bold tracking-wider ${isOutbound ? "text-blue-600" : "text-emerald-600"}">
                     ${isOutbound ? "Tú enviando" : (m.contacts?.name || "Recibido")}
                   </div>
                   <div class="text-[10px] text-slate-400 font-medium whitespace-nowrap ml-2">${date} ${time}</div>
                </div>
                <div class="text-sm font-bold text-slate-900 line-clamp-1 mb-1">${escapeHtml(m.subject || "(Sin asunto)")}</div>
                <div class="text-[11px] text-slate-500 line-clamp-2 leading-relaxed">
                  ${escapeHtml(m.body || "").replace(/\n/g, " ")}
                </div>
             </div>
          </div>
        </div>
      `;
    }).join("");

    listEl.querySelectorAll(".email-list-item").forEach((item) => {
      const emailId = item.dataset.id;
      const m = emails.find(e => e.id === emailId);

      item.onclick = () => {
        listEl.querySelectorAll(".email-list-item").forEach(i => i.classList.remove("bg-blue-50/60", "border-l-4", "border-l-blue-500", "pl-[12px]"));
        listEl.querySelectorAll(".email-list-item").forEach(i => i.classList.add("pl-4"));

        item.classList.add("bg-blue-50/60", "border-l-4", "border-l-blue-500");
        item.classList.replace("pl-4", "pl-[12px]");

        emptyState.classList.add("hidden");
        detailsPane.classList.remove("hidden");

        const isOutbound = m.direction === "outbound";
        const dateObj = new Date(m.created_at);
        const fullDate = dateObj.toLocaleDateString("es-ES", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
        const time = dateObj.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
        const badgeColor = isOutbound ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700";

        detailsPane.innerHTML = `
          <div class="flex items-start justify-between mb-6 pb-6 border-b border-slate-100">
             <div>
                <h2 class="text-xl font-bold text-slate-900 mb-2">${escapeHtml(m.subject || "(Sin asunto)")}</h2>
                <div class="flex items-center gap-3">
                  <span class="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${badgeColor}">${isOutbound ? "Enviado por ti" : "Recibido de Cliente"}</span>
                  ${!isOutbound && m.contacts?.name ? `<span class="text-sm font-medium text-slate-600">${escapeHtml(m.contacts.name)} &lt;${escapeHtml(m.contacts.email || "")}&gt;</span>` : ""}
                </div>
             </div>
             <div class="text-xs text-slate-500 font-medium whitespace-nowrap text-right">
                <div>${fullDate}</div>
                <div>${time}</div>
             </div>
          </div>
          
          <div class="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
            ${escapeHtml(m.body || "")}
          </div>
        `;
      };
    });
  } catch (err) {
    console.error(err);
    listEl.innerHTML = `
      <div class="p-4 m-4 bg-red-50 border border-red-100 rounded-xl text-center">
        <p class="text-xs text-red-600">Error al cargar el historial de correos.</p>
      </div>
    `;
  }
}
